import React, { useState } from 'react';
import styled from 'styled-components';
import { supabase } from '../supabaseClient';

const FormContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  width: 350px;
  max-width: 90%;
  margin: 20px auto;
  text-align: center;
  z-index: 1001;
  position: relative;
  pointer-events: auto;
`;

const Title = styled.h2`
  margin-bottom: 15px;
  color: #333;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  pointer-events: auto;
`;

const TextArea = styled.textarea`
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  resize: vertical;
  min-height: 100px;
  font-family: 'Arial', sans-serif;
  pointer-events: auto;
  
  &:focus {
    outline: none;
    border-color: #9ecaed;
    box-shadow: 0 0 5px #9ecaed;
  }
`;

const Input = styled.input`
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  pointer-events: auto;
  
  &:focus {
    outline: none;
    border-color: #9ecaed;
    box-shadow: 0 0 5px #9ecaed;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  justify-content: center;
  pointer-events: auto;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  pointer-events: auto;
  cursor: pointer;
`;

const Label = styled.label`
  color: #555;
  pointer-events: auto;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #357abd;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-bottom: 15px;
`;

const SuccessMessage = styled.div`
  color: #2ecc71;
  margin-bottom: 15px;
`;

const MessageForm = ({ onMessageSubmitted }) => {
  const [message, setMessage] = useState('');
  const [author, setAuthor] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      console.log('Submitting message to HealingOutLoud table...');
      const messageData = { 
        message, 
        author: isAnonymous ? null : author || null
      };
      console.log('Message data:', messageData);
      
      const { data, error } = await supabase
        .from('HealingOutLoud')
        .insert([messageData])
        .select();
      
      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }
      
      console.log('Message submitted successfully:', data);
      setSuccess('Your message has been shared. Thank you!');
      setMessage('');
      setAuthor('');
      
      if (onMessageSubmitted && data) {
        onMessageSubmitted(data[0]);
      }
      
      // Show success message briefly before refreshing
      setTimeout(() => {
        // Refresh the page to show the latest notes
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      setError('Failed to submit your message. Please try again.');
      console.error('Error submitting message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer 
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Title>Share Your Healing Journey</Title>
      <StyledForm 
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
      >
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <TextArea
          placeholder="Share your thoughts, feelings, or reflections on your healing journey..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={500}
          required
          onClick={(e) => e.stopPropagation()}
        />
        
        {!isAnonymous && (
          <Input
            type="text"
            placeholder="Your name (optional)"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            maxLength={50}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        
        <CheckboxContainer onClick={(e) => e.stopPropagation()}>
          <Checkbox
            type="checkbox"
            id="anonymous"
            checked={isAnonymous}
            onChange={() => setIsAnonymous(!isAnonymous)}
            onClick={(e) => e.stopPropagation()}
          />
          <Label 
            htmlFor="anonymous"
            onClick={(e) => {
              e.stopPropagation();
              setIsAnonymous(!isAnonymous);
            }}
          >
            Post anonymously
          </Label>
        </CheckboxContainer>
        
        <SubmitButton 
          type="submit" 
          disabled={isSubmitting}
          onClick={(e) => e.stopPropagation()}
        >
          {isSubmitting ? 'Submitting...' : 'Share Your Message'}
        </SubmitButton>
      </StyledForm>
    </FormContainer>
  );
};

export default MessageForm; 